import { useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import type { Case } from '../domain/initial-cases';
import { attachCaseCustomer } from '../api/cases';
import { useAppData } from '../state/contexts/AppDataContext';
import { useCustomerDirectory } from '../state/customerDirectory';
import { CustomerLabel, caseCustomerId } from './CustomerLabel';

// #514/#516 — the owning customer in the case header, and the one place a case
// can be linked to one.
//
// WHY THE CONTROL LIVES HERE rather than in a create-time form: there is no
// creation form. "+ New Case" and "+ New Application" are single-click actions,
// and every case that existed before #514 is un-linked anyway — so the header
// is the surface that serves both the backlog and anything created from a
// button. The API accepts a customer at creation too, for callers that have a
// form of their own.
//
// LINKING IS NOT UNDOABLE FROM HERE, and the confirmation says so rather than
// burying it. The moment the link lands, that company's portal can read this
// case AND EVERYTHING THAT HAS EVER HAPPENED ON IT, and the case joins that
// company's change propagation. The server refuses a second, different customer
// with a 409; this text is what stops somebody discovering that by trying.

export function CaseCustomerChip({ c }: { c: Case }): JSX.Element {
  const directory = useCustomerDirectory();
  const { mergeLocalCase } = useAppData();
  const [picking, setPicking] = useState(false);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linked = caseCustomerId(c);

  if (linked) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: C.textDim }}>Customer:</span>
        <CustomerLabel customerId={linked} name={directory.nameOf(linked)} variant="chip" />
      </span>
    );
  }

  if (!picking) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <CustomerLabel customerId={null} name={null} variant="chip" />
        <button
          type="button"
          style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
          onClick={() => { setPicking(true); setError(null); }}
        >
          Link customer
        </button>
      </span>
    );
  }

  const attach = async (): Promise<void> => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      const res = await attachCaseCustomer(c.id, choice);
      // Fold the server's answer into the store rather than PATCHing: a PATCH
      // would send the whole `onboarding` column back and is exactly what the
      // dedicated endpoint exists to avoid. `version` comes from the server
      // because the attach bumped it.
      mergeLocalCase(c.id, {
        onboarding: { ...(c.onboarding ?? {}), customerId: res.customerId },
        version: res.version,
      } as Partial<Case>);
      setPicking(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <label htmlFor="case-customer-select" style={{ fontSize: 11, color: C.textDim }}>Link to</label>
      <select
        id="case-customer-select"
        style={{ ...styles.input, width: 'auto', fontSize: 12 }}
        value={choice}
        disabled={busy}
        onChange={(e) => setChoice(e.target.value)}
      >
        <option value="">Select a company…</option>
        {directory.all.map((cust) => (
          // A SUSPENDED company is listed and labelled rather than hidden: the
          // server refuses the link with a 422 that says so, and a company
          // silently missing from this list is a support call.
          <option key={cust.customerId} value={cust.customerId}>
            {cust.name}{cust.status === 'SUSPENDED' ? ' (suspended)' : ''}
          </option>
        ))}
      </select>
      <button
        type="button"
        style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
        disabled={!choice || busy}
        onClick={() => { void attach(); }}
      >
        {busy ? 'Linking…' : 'Link'}
      </button>
      <button
        type="button"
        style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
        disabled={busy}
        onClick={() => { setPicking(false); setError(null); }}
      >
        Cancel
      </button>
      <span style={{ fontSize: 11, color: C.textDim, width: '100%' }}>
        Linking lets this company see the case and its whole history, and cannot be changed here afterwards.
      </span>
      {error && <span role="alert" style={{ fontSize: 11, color: C.danger, width: '100%' }}>{error}</span>}
    </span>
  );
}
